import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Plus, ChevronRight, ChevronLeft, Save, Send, FileText } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { StatusBadge } from "../components/StatusBadge";
import { CardSkeleton, EmptyState } from "../components/Skeletons";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const SIZE_OPTIONS = ["XS", "S", "M", "L", "XL", "XXL"];

function StepDots({ step, total = 4 }) {
  return (
    <div className="flex items-center gap-2" data-testid="step-indicator">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`k-step-dot rounded-full h-2 transition-all ${i < step ? "bg-[#534AB7] w-8" : i === step ? "w-8 active bg-[#534AB7]" : "w-2 bg-muted"}`}
        />
      ))}
    </div>
  );
}

function RequestForm({ open, onClose, onCreated, draft }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    department_id: "",
    product_id: "",
    item_id: "",
    brand: "",
    design: "",
    color: "",
    fabric: "",
    pattern: "",
    city: "",
    vendor_id: "",
    procurement_type: "new",
    sizes: [],
    notes: "",
  });
  const [departments, setDepartments] = useState([]);
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(draft?.id || null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [d, p, i, v, inv] = await Promise.all([
        api.get("/departments"), api.get("/products"), api.get("/items"), api.get("/vendors"), api.get("/inventory"),
      ]);
      setDepartments(d.data);
      setProducts(p.data);
      setItems(i.data);
      setVendors(v.data);
      setInventory(inv.data);
    })();
  }, [open]);

  useEffect(() => {
    if (draft) {
      setForm({ ...draft, sizes: draft.sizes || [] });
      setSavedId(draft.id);
    } else {
      setStep(0);
      setForm({ department_id: "", product_id: "", item_id: "", brand: "", design: "", color: "",
        fabric: "", pattern: "", city: "", vendor_id: "", procurement_type: "new", sizes: [], notes: "" });
      setSavedId(null);
    }
  }, [draft, open]);

  // Auto-save draft every 30s
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => {
      if (form.department_id && form.product_id) saveDraft(true);
    }, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form]);

  const itemsForProduct = useMemo(
    () => items.filter((it) => it.product_id === form.product_id),
    [items, form.product_id]
  );

  const stockBySize = useMemo(() => {
    const m = {};
    inventory
      .filter((x) => x.item_id === form.item_id)
      .forEach((x) => (m[x.size] = x.qty));
    return m;
  }, [inventory, form.item_id]);

  const toggleSize = (size) => {
    const existing = form.sizes.find((s) => s.size === size);
    if (existing) {
      setForm({ ...form, sizes: form.sizes.filter((s) => s.size !== size) });
    } else {
      setForm({ ...form, sizes: [...form.sizes, { size, quantity: 10 }] });
    }
  };

  const updateSizeQty = (size, qty) => {
    setForm({
      ...form,
      sizes: form.sizes.map((s) => (s.size === size ? { ...s, quantity: parseInt(qty || 0) } : s)),
    });
  };

  const totalQty = form.sizes.reduce((a, s) => a + (s.quantity || 0), 0);

  const onItemSelect = (itemId) => {
    const it = items.find((x) => x.id === itemId);
    setForm({
      ...form,
      item_id: itemId,
      brand: it?.brand || form.brand,
      design: it?.design || form.design,
      color: it?.color || form.color,
      fabric: it?.fabric || form.fabric,
      pattern: it?.pattern || form.pattern,
    });
  };

  const saveDraft = async (silent = false) => {
    setSaving(true);
    try {
      const payload = { ...form, is_draft: true };
      let result;
      if (savedId) {
        result = await api.patch(`/requests/${savedId}`, payload);
      } else {
        result = await api.post("/requests", payload);
        setSavedId(result.data.id);
      }
      if (!silent) toast.success("Draft saved");
    } catch (e) {
      if (!silent) toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (!form.department_id || !form.product_id || form.sizes.length === 0) {
      toast.error("Please fill department, product, and at least one size");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, is_draft: false };
      if (savedId) await api.patch(`/requests/${savedId}`, payload);
      else await api.post("/requests", payload);
      toast.success("Request submitted for approval");
      onCreated?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    {
      title: "Department & type",
      content: (
        <div className="space-y-4">
          <div>
            <Label>Department</Label>
            <Select value={form.department_id} onValueChange={(v) => setForm({ ...form, department_id: v })}>
              <SelectTrigger data-testid="req-department"><SelectValue placeholder="Choose department" /></SelectTrigger>
              <SelectContent>
                {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Procurement type</Label>
            <div className="flex gap-2 mt-1">
              {["new", "refill"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm({ ...form, procurement_type: t })}
                  className={`px-4 py-2 rounded-lg text-sm border transition ${form.procurement_type === t ? "bg-[#534AB7] text-white border-transparent" : "border-border hover:bg-foreground/5"}`}
                  data-testid={`req-type-${t}`}
                >
                  {t === "new" ? "New Purchase" : "Refill"}
                </button>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Product",
      content: (
        <div className="space-y-4">
          <div>
            <Label>Product</Label>
            <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v, item_id: "" })}>
              <SelectTrigger data-testid="req-product"><SelectValue placeholder="Choose product" /></SelectTrigger>
              <SelectContent>
                {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Item</Label>
            <Select value={form.item_id} onValueChange={onItemSelect} disabled={!form.product_id}>
              <SelectTrigger data-testid="req-item"><SelectValue placeholder={form.product_id ? "Choose item" : "Select product first"} /></SelectTrigger>
              <SelectContent>
                {itemsForProduct.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      ),
    },
    {
      title: "Item details",
      content: (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            ["brand", "Brand"], ["design", "Design"], ["color", "Color"],
            ["fabric", "Fabric"], ["pattern", "Pattern"], ["city", "City"],
          ].map(([k, l]) => (
            <div key={k}>
              <Label>{l}</Label>
              <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`req-${k}`} />
            </div>
          ))}
          <div className="sm:col-span-2">
            <Label>Vendor</Label>
            <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
              <SelectTrigger data-testid="req-vendor"><SelectValue placeholder="Choose vendor" /></SelectTrigger>
              <SelectContent>
                {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name} • {v.city}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      ),
    },
    {
      title: "Sizes & quantity",
      content: (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Tap a size to add it. Stock-on-hand shown per size.</p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="size-grid">
            {SIZE_OPTIONS.map((s) => {
              const selected = form.sizes.some((x) => x.size === s);
              const stock = stockBySize[s];
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSize(s)}
                  className={`p-3 rounded-xl border text-sm transition k-btn-press ${selected ? "bg-[#534AB7] text-white border-transparent" : "border-border hover:bg-foreground/5"}`}
                  data-testid={`size-${s}`}
                >
                  <div className="font-semibold">{s}</div>
                  <div className={`text-[10px] mt-1 ${selected ? "text-white/80" : "text-muted-foreground"}`}>
                    Stock: {stock ?? "-"}
                  </div>
                </button>
              );
            })}
          </div>
          {form.sizes.length > 0 && (
            <div className="k-glass rounded-xl p-4 space-y-2">
              {form.sizes.map((s) => (
                <div key={s.size} className="flex items-center gap-3">
                  <span className="w-12 font-medium">{s.size}</span>
                  <Input
                    type="number"
                    min={0}
                    value={s.quantity}
                    onChange={(e) => updateSizeQty(s.size, e.target.value)}
                    className="w-28"
                    data-testid={`qty-${s.size}`}
                  />
                  <span className="text-xs text-muted-foreground">units</span>
                </div>
              ))}
            </div>
          )}
          <div>
            <Label>Notes (optional)</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="req-notes" />
          </div>
        </div>
      ),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New procurement request</DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-6" data-testid="req-form-step">
            <StepDots step={step} total={steps.length} />
            <div>
              <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: "Outfit" }}>
                {steps[step].title}
              </h3>
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {steps[step].content}
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="flex flex-wrap justify-between gap-2 pt-2">
              <Button variant="ghost" onClick={() => saveDraft()} disabled={saving} className="gap-2" data-testid="save-draft">
                <Save size={14} /> Save draft
              </Button>
              <div className="flex gap-2">
                {step > 0 && (
                  <Button variant="outline" onClick={() => setStep(step - 1)} className="gap-1" data-testid="step-back">
                    <ChevronLeft size={14} /> Back
                  </Button>
                )}
                {step < steps.length - 1 ? (
                  <Button onClick={() => setStep(step + 1)} className="bg-[#534AB7] hover:bg-[#463E9F] gap-1" data-testid="step-next">
                    Next <ChevronRight size={14} />
                  </Button>
                ) : (
                  <Button onClick={submit} disabled={saving} className="bg-[#1D9E75] hover:bg-[#178A65] gap-1" data-testid="submit-request">
                    <Send size={14} /> Submit
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Live summary */}
          <aside className="k-glass rounded-2xl p-4 space-y-3" data-testid="req-summary">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Live summary</div>
            <div className="space-y-2 text-sm">
              <SummaryRow label="Department" value={departments.find(d => d.id === form.department_id)?.name} />
              <SummaryRow label="Type" value={form.procurement_type} />
              <SummaryRow label="Product" value={products.find(p => p.id === form.product_id)?.name} />
              <SummaryRow label="Item" value={items.find(i => i.id === form.item_id)?.name} />
              <SummaryRow label="Brand" value={form.brand} />
              <SummaryRow label="Vendor" value={vendors.find(v => v.id === form.vendor_id)?.name} />
              <div className="pt-2 border-t border-border">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total qty</span>
                  <span className="text-2xl font-bold" style={{ fontFamily: "Outfit", color: "#534AB7" }}>{totalQty}</span>
                </div>
              </div>
              {form.sizes.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {form.sizes.map((s) => (
                    <span key={s.size} className="text-xs px-2 py-0.5 rounded-full bg-[#534AB7]/10 text-[#534AB7]">
                      {s.size}: {s.quantity}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground pt-2">Auto-saved every 30s</div>
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right truncate max-w-[60%]">{value || "—"}</span>
    </div>
  );
}

export default function Requests() {
  const [requests, setRequests] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");

  const load = async () => {
    setLoading(true);
    const params = filterStatus === "all" ? {} : { status: filterStatus };
    const [r, d, dep] = await Promise.all([
      api.get("/requests", { params }),
      api.get("/requests/drafts"),
      api.get("/departments"),
    ]);
    setRequests(r.data.items || []);
    setDrafts(d.data || []);
    setDepartments(dep.data || []);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus]);

  const deptName = (id) => departments.find((d) => d.id === id)?.name || "—";

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }} data-testid="requests-title">
            Procurement requests
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Capture demand, route for approval.</p>
        </div>
        <div className="flex gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 k-glass" data-testid="filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => { setEditingDraft(null); setOpen(true); }} className="bg-[#534AB7] hover:bg-[#463E9F] gap-2" data-testid="new-request-btn">
            <Plus size={16} /> New request
          </Button>
        </div>
      </div>

      {drafts.length > 0 && (
        <div className="k-glass rounded-2xl p-4" data-testid="drafts-section">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <FileText size={14} /> Drafts ({drafts.length})
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {drafts.map((d) => (
              <button
                key={d.id}
                onClick={() => { setEditingDraft(d); setOpen(true); }}
                className="text-left p-3 rounded-xl border border-border hover:bg-foreground/5 transition"
                data-testid={`draft-${d.id}`}
              >
                <div className="text-sm font-medium truncate">{d.brand || "Untitled"}</div>
                <div className="text-xs text-muted-foreground">{deptName(d.department_id)} • {d.total_qty || 0} units</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
      ) : requests.length === 0 ? (
        <EmptyState
          title="No requests yet"
          description="Create your first procurement request to get started."
          icon={FileText}
          action={<Button onClick={() => setOpen(true)} className="bg-[#534AB7] hover:bg-[#463E9F]" data-testid="empty-new-request">New request</Button>}
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {requests.map((r) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -2 }}
              className="k-glass rounded-2xl p-4 space-y-2"
              data-testid={`request-card-${r.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#534AB7]/15 text-[#534AB7] flex items-center justify-center text-xs font-semibold">
                    {(r.requester_name || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="text-xs">
                    <div className="font-medium">{r.requester_name}</div>
                    <div className="text-muted-foreground">{deptName(r.department_id)}</div>
                  </div>
                </div>
                <StatusBadge status={r.status} />
              </div>
              <div>
                <div className="text-base font-semibold">{r.brand || "—"}</div>
                <div className="text-xs text-muted-foreground">{r.design} · {r.color} · {r.fabric}</div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-border/50">
                <span className="text-xs text-muted-foreground">{r.procurement_type === "new" ? "New" : "Refill"} • {r.sizes?.length || 0} sizes</span>
                <span className="text-lg font-bold" style={{ fontFamily: "Outfit", color: "#534AB7" }}>{r.total_qty}</span>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <RequestForm open={open} onClose={() => setOpen(false)} onCreated={load} draft={editingDraft} />
    </div>
  );
}
